<template>
  <div class="row">
    <div class="col-12">
      <div class="col-md-12">
        <create-edit-project-form
          v-if="showCreateProjectForm && userRole !== 'User'"
          :edit-mode="editMode"
          :key-prop="editKey"
          :name-prop="editName"
          :description-prop="editDescription"
          :editing-project-id="editProjectId"
          :start-date-prop="editProjectStartTime"
          :end-date-prop="editProjectEndTime"
          :selected-project-status-option-prop="editProjectStatus"
          :end-customer-id-prop="editEndCustomerId"
          :end-customer-name-prop="editEndCustomerName"
          :force-end-customer-selection="forceEndCustomerSelection"
          :address-prop="editAddress"
          :zip-code-prop="editZipCode"
          @close="closeCreateProjectForm"
          @refresh-data="fetchData"
        />
      </div>
      <card
        :title="$t('projects.projects')"
        :on-custom-method="customMethod"
        :custom-method-icon="customIcon"
        :custom-method-image="getERPImage"
        :custom-method-icon-spin="loading"
      >
        <template v-if="enableExcelExport && userRole !== 'User'" #under-header>
          <div style="padding: 0 0 8px;">
            <el-button :icon="Download" size="small" @click="openExportDialog('projects')">{{ $t('dataExport.exportProjects') }}</el-button>
          </div>
        </template>
        <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mb-2">
          <div class="col-12 col-md-4 col-sm-6 mb-2">
            <el-input
              v-model="searchQuery"
              class="search-input"
              clearable
              :prefix-icon="Search"
              :placeholder="$t('common.searchRecords')"
              aria-controls="datatables"
              @keyup="search"
              @clear="handleClearSearch"
            />
          </div>
          <div v-if="userRole !== 'User'" class="col-12 col-md-3 col-sm-6 mb-2">
            <el-select
              v-model="statusFilter"
              :class="selectClass"
              :placeholder="$t('projectStatus.all')"
              class="w-100"
              @change="fetchData"
            >
              <template #prefix>{{ $t('common.status') }}:</template>
              <el-option
                v-for="status in statusOptions"
                :key="status.id"
                :class="selectClass"
                :label="status.status"
                :value="status.status"
              />
            </el-select>
          </div>
        </div>

        <paginated-table
          :columns="tableColumns"
          :data="projects"
          :pagination="pagination"
          :ready="dataReady"
          :allow-click="true"
          :enable-edit="enableProjectCreation && userRole !== 'User'"
          :enable-delete="false"
          :enable-custom-action="enableProjectPhases"
          :custom-action="openPhasesModal"
          :custom-action-icon="'Share'"
          :should-show-custom-action="(row) => row.phasesCount > 0"
          :actions-inline="true"
          @row-click="goToProject"
          @edit="handleEdit"
          @update-page="updatePage"
        />
      </card>
    </div>

    <data-export-modal
      v-model:show="showExportDialog"
      :type="exportDialogType"
    />

    <!-- Read-only phases list (phases are synced from the ERP) -->
    <modal :show="showPhasesModal" class="white-content phases-modal" @update:show="showPhasesModal = $event">
      <h5 class="card-title mb-0">{{ $t('projects.phases') }}</h5>
      <p v-if="phasesModalProject" class="phases-modal-subtitle">
        <span v-if="phasesModalProject.key" class="text-secondary">[{{ phasesModalProject.key }}]</span>
        {{ phasesModalProject.name }}
      </p>
      <div class="phases-modal-list">
        <div v-for="phase in phasesModalPhases" :key="phase.id" class="phases-modal-item">
          <span class="phases-modal-dot"></span>
          <div>
            <div class="phases-modal-name">{{ phase.name }}</div>
            <div v-if="phase.description" class="phases-modal-description">{{ phase.description }}</div>
          </div>
        </div>
        <p v-if="!phasesLoading && phasesModalPhases.length === 0" class="text-secondary text-sm mb-0">{{ $t('projects.noPhases') }}</p>
      </div>
    </modal>
  </div>
</template>

<script>
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import api from "../../services/api";
import { loadCustomerMeCached } from '../../services/customerMeHelper';
import { ElInput, ElSelect, ElOption, ElButton } from 'element-plus';
import { Search, Download } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import Card from "../../components/Card.vue";
import PaginatedTable from "../../components/PaginatedTable.vue";
import CreateEditProjectForm from "./CreateEditProjectForm.vue";
import DataExportModal from "../../components/DataExportModal.vue";
import Modal from "../../components/Modals/Modal.vue";

export default {
  components: {
    ElInput,
    ElSelect,
    ElOption,
    ElButton,
    Card,
    PaginatedTable,
    CreateEditProjectForm,
    DataExportModal,
    Modal,
  },
  data() {
    return {
      Search: shallowRef(Search),
      Download: shallowRef(Download),
      showExportDialog: false,
      exportDialogType: 'projects',
      enableExcelExport: false,

      // Edit properties
      editKey: '',
      editName: '',
      editDescription: '',
      editProjectId: '',
      editProjectStartTime: null,
      editProjectEndTime: null,
      editProjectStatus: this.$t('projectStatus.notStarted'),
      editEndCustomerId: null,
      editEndCustomerName: null,
      editAddress: '',
      editZipCode: '',
      editMode: false,

      forceEndCustomerSelection: false,
      enableProjectPhases: false,
      showPhasesModal: false,
      phasesModalProject: null,
      phasesModalPhases: [],
      phasesLoading: false,
      customerId: '',
      erpName: '',
      loading: false,
      enableProjectCreation: false,
      showCreateProjectForm: false,
      userRole: '',
      dataReady: false,
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0
      },
      searchQuery: '',
      projects: [],
      isMobile: window.innerWidth <= 1024,
      statusFilter: '',
      statusOptions: [
        { id: 0, status: this.$t('projectStatus.all') },
        { id: 1, status: this.$t('projectStatus.notStarted') },
        { id: 2, status: this.$t('projectStatus.active') },
        { id: 3, status: this.$t('projectStatus.onHold') },
        { id: 4, status: this.$t('projectStatus.finished') },
        { id: 5, status: this.$t('projectStatus.other') },
      ],
      tableColumns: [
        { prop: 'key', label: this.$t('projects.key'), minWidth: 100 },
        { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
        { prop: 'description', label: this.$t('common.description'), minWidth: 100 },
        { prop: 'status', label: this.$t('common.status'), minWidth: 100 },
        { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'projects.key': 'key', 'common.name': 'name', 'common.description': 'description', 'common.status': 'status' } },
      ],
    };
  },
  computed: {
    customIcon() {
      if (this.userRole === 'User') return '';
      return this.enableProjectCreation ? 'add' : 'sync';
    },
    getERPImage() {
      switch (this.erpName) {
        case 'DK':
        case 'DKProjectRequests':
        case 'DKFinnur':
        case 'DKWithProjectPhases':
          return 'img/dk-logo2.png';
        case 'Tímon':
          return 'img/timon.png';
        case 'Stólpi':
          return 'img/stolpi.png';
        case 'BusinessCentralCustomers':
          return 'img/business-central-logo.png';
        case 'ReglaIs':
          return 'img/Regla_logo.png';
        case 'Verklag':
          return 'img/verklag_logo.png';
        default:
          return '';
      }
    },
    customMethod() {
      if (this.userRole === 'User') return this.dummyMethod;
      return this.enableProjectCreation ? this.openCreateProjectForm : this.sync;
    },
    selectClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'select-primary' : 'select-standard';
    },
  },
  created() {
    if (this.isMobile) {
      this.statusFilter = this.$t('projectStatus.all');
    }

    const savedSearchQuery = Cookies.get('projectsSearchQuery');
    if (savedSearchQuery) {
      this.searchQuery = savedSearchQuery;
    }

    const savedPage = Cookies.get('projectsPage');
    if (savedPage) {
      this.pagination.currentPage = Number(savedPage);
    }

    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role;
    this.customerId = decodedToken.CustomerId;

    if (this.userRole === 'User') {
      this.statusFilter = this.$t('projectStatus.active');
      this.tableColumns = [
        { prop: 'key', label: this.$t('projects.key'), minWidth: 100 },
        { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
        { prop: 'description', label: this.$t('common.description'), minWidth: 100 },
        { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'projects.key': 'key', 'common.name': 'name', 'common.description': 'description' } },
      ];
    }

    getCustomerConfig('EnableProjectCreation').then(config => {
      this.enableProjectCreation = config === '' ? false : config;
    });

    getCustomerConfig('ForceEndCustomerSelectionOnProjectCreation').then(config => {
      this.forceEndCustomerSelection = config === true;
      if (this.forceEndCustomerSelection && this.userRole !== 'User') {
        this.tableColumns.splice(this.tableColumns.length - 1, 0, { prop: 'endCustomerName', label: this.$t('endCustomers.endCustomer'), minWidth: 150 });
      }
    });

    getCustomerConfig('EnableProjectPhases').then(config => {
      this.enableProjectPhases = config === true;
      if (this.enableProjectPhases) {
        this.tableColumns.splice(this.tableColumns.length - 1, 0, { prop: 'phasesCount', label: this.$t('projects.phases'), minWidth: 80 });
      }
    });

    // Get ERP
    if (Cookies.get(this.customerId + '-erpName')) {
      this.erpName = Cookies.get(this.customerId + '-erpName');
    } else {
      loadCustomerMeCached()
        .then(customer => {
          if (customer) {
            Cookies.set(this.customerId + '-erpName', customer.erpName);
            this.erpName = customer.erpName;
          } else {
            Cookies.set(this.customerId + '-erpName', '');
          }
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 2000 });
        });
    }

    getCustomerConfig('DefaultPageSize').then(config => {
      this.pagination.perPage = config === '' ? 10 : config;
      this.fetchData();
    });
    getCustomerConfig('EnableExcelExport').then(config => {
      this.enableExcelExport = config === '' ? false : config;
    });
    window.addEventListener('resize', this.updateIsMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    openExportDialog(type) {
      this.exportDialogType = type;
      this.showExportDialog = true;
    },
    updateIsMobile() {
      this.isMobile = window.innerWidth <= 1024;
    },
    dummyMethod() {},

    search(event) {
      this.pagination.currentPage = 1;
      const queryLength = this.searchQuery.length;
      if (event.key === 'Backspace' || event.key === 'Enter') {
        this.fetchData();
      } else if (queryLength > 1 && queryLength % 2 === 0) {
        this.fetchData();
      }
    },

    updatePage(page) {
      this.pagination.currentPage = page;
      Cookies.set('projectsPage', page, { expires: new Date(new Date().getTime() + 60000) });
      this.fetchData();
    },

    openCreateProjectForm() {
      this.showCreateProjectForm = true;
      this.$nextTick(() => window.scrollTo(0, 0));
    },

    closeCreateProjectForm() {
      this.showCreateProjectForm = false;
      this.editMode = false;
      this.editDescription = '';
      this.editName = '';
      this.editKey = '';
      this.editProjectStatus = this.$t('projectStatus.notStarted');
      this.editProjectId = '';
      this.editProjectEndTime = null;
      this.editProjectStartTime = null;
      this.editEndCustomerId = null;
      this.editEndCustomerName = null;
      this.editAddress = '';
      this.editZipCode = '';
    },

    handleClearSearch() {
      Cookies.remove('projectsPage');
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    goToProject(item) {
      this.$router.push({ name: 'Project', params: { projectId: item.id } });
    },

    async openPhasesModal(index, row) {
      this.phasesModalProject = row;
      this.phasesModalPhases = [];
      this.phasesLoading = true;
      this.showPhasesModal = true;
      try {
        const response = await api.get(`/Project/${row.id}/Phases`);
        this.phasesModalPhases = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 2000 });
      }
      this.phasesLoading = false;
    },

    handleEdit(index, row) {
      if (row.erpSystemId !== null && row.erpSystemId !== undefined) {
        return;
      }
      this.editMode = true;
      this.editDescription = row.description;
      this.editName = row.name;
      this.editKey = row.key;
      this.editProjectStatus = row.status;
      this.editProjectId = row.id;
      this.editProjectEndTime = row.endTime;
      this.editProjectStartTime = row.startTime;
      this.editEndCustomerId = row.endCustomerId;
      this.editEndCustomerName = row.endCustomerName;
      this.editAddress = row.address || '';
      this.editZipCode = row.zipCode || '';
      this.openCreateProjectForm();
    },

    fetchData() {
      this.dataReady = false;
      let projectUrl = '';
      if (this.searchQuery === '') {
        Cookies.remove('projectsSearchQuery');
        projectUrl = `/Project?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}`;
      } else {
        Cookies.set('projectsSearchQuery', this.searchQuery, { expires: new Date(new Date().getTime() + 60000) });
        projectUrl = `/Project?currentPage=${this.pagination.currentPage}&pageSize=${this.pagination.perPage}&searchString=${this.searchQuery}`;
      }

      if (this.statusFilter !== this.$t('projectStatus.all') && this.statusFilter !== '') {
        const statusOption = this.statusOptions.find(op => op.status === this.statusFilter);
        if (statusOption) {
          projectUrl += `&projectStatusId=${statusOption.id}`;
        }
      }

      api.get(projectUrl)
        .then(response => {
          this.pagination.total = response.data.totalItems;
          this.projects = response.data.result.map(item => ({
            id: item.id,
            key: item.key,
            name: item.name,
            description: item.description,
            status: this.getStatusTranslation(item.projectStatusId),
            startTime: item.startTime,
            endTime: item.endTime,
            erpSystemId: item.erpSystemId,
            endCustomerId: item.endCustomerId,
            endCustomerName: item.endCustomerName,
            address: item.address,
            zipCode: item.zipCode,
            phasesCount: item.phasesCount || 0,
            canEdit: item.erpSystemId === null || item.erpSystemId === undefined,
          }));
          this.dataReady = true;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
    },

    sync() {
      this.loading = true;
      api.post('/Project/Sync')
        .then(response => {
          if (response.data.success) {
            this.$toast.success(
              `${response.data.projectsCreated} ${this.$t('projects.projectsCreated')} - ${response.data.projectsUpdated} ${this.$t('projects.projectsUpdated')}`,
              { timeout: 2000 }
            );
            this.fetchData();
          } else {
            this.$toast.danger(this.$t('common.somethingWentWrong') + ' - ' + response.data.errorMessage, { timeout: 0 });
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.loading = false;
          this.$toast.danger(this.$t('common.somethingWentWrong'), { timeout: 0 });
        });
    },

    getStatusTranslation(statusId) {
      switch (statusId) {
        case 1: return this.$t('projectStatus.notStarted');
        case 2: return this.$t('projectStatus.active');
        case 3: return this.$t('projectStatus.onHold');
        case 4: return this.$t('projectStatus.finished');
        case 5: return this.$t('projectStatus.other');
        default: return '';
      }
    },
  },
};
</script>

<style scoped>
.phases-modal-subtitle {
  margin: 2px 0 12px;
  font-size: 0.85rem;
  font-weight: 600;
}
.phases-modal-list {
  max-height: 50vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.phases-modal-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 10px;
}
.phases-modal-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: #344767;
}
.phases-modal-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #344767;
}
.phases-modal-description {
  font-size: 0.8rem;
  color: #67748e;
}
</style>
